import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { authClient } from "@/lib/auth-client";
import { AppShell, FeedbackState } from "@h2j-data-team/ui";
import {
  AccountMenu,
  AppNavigation,
  OrgSwitcher,
  useActiveOrganization,
} from "@/components/app-sidebar";
import { PRODUCT_NAME } from "@repo/shared";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async () => {
    const session = await authClient.getSession();

    if (!session.data) {
      throw redirect({
        to: "/login",
        search: {
          mode: undefined,
          token: undefined,
          error: undefined,
          redirect: undefined,
          email: undefined,
        },
      });
    }

    if (!session.data.user.emailVerified) {
      throw redirect({ to: "/verify-email", search: { email: session.data.user.email } });
    }

    return { user: session.data.user };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user } = Route.useRouteContext();
  const matches = useMatches();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { t } = useTranslation("nav");
  const organization = useActiveOrganization();
  const hasNoOrg = !organization.isLoading && organization.memberships.length === 0;

  const pageTitle =
    [...matches].reverse().find((m) => m.staticData?.pageTitle)?.staticData?.pageTitle ?? null;
  const isBillingRoute =
    pathname === "/organization/billing" ||
    pathname === "/organization/settings" ||
    pathname === "/profile";
  const shouldBlockAppContent = !isBillingRoute && hasNoOrg;
  // Below 768px the navigation opens over the page; it belongs to the path it
  // was opened on, so following a link closes it.
  const [navigationOpenAt, setNavigationOpenAt] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle ? `${t(pageTitle)} — ${PRODUCT_NAME}` : PRODUCT_NAME;
  }, [pageTitle, t]);

  return (
    <AppShell
      brand={<OrgSwitcher organization={organization} />}
      navigation={<AppNavigation organization={organization} />}
      account={<AccountMenu user={user} />}
      navigationLabel={t("shell.navigation", { ns: "common" })}
      openNavigationLabel={t("shell.openNavigation", { ns: "common" })}
      closeNavigationLabel={t("shell.closeNavigation", { ns: "common" })}
      skipLinkLabel={t("shell.skipToContent", { ns: "common" })}
      navigationOpen={navigationOpenAt === pathname}
      onNavigationOpenChange={(open) => setNavigationOpenAt(open ? pathname : null)}
    >
      {shouldBlockAppContent ? (
        <FeedbackState state="empty" title={t("noActiveOrganization")} />
      ) : (
        // Keyed by organization: switching remounts the screens, so local state
        // (page, cursors, open items) never carries over to the other organization.
        <Outlet key={organization.active?.id ?? "none"} />
      )}
    </AppShell>
  );
}
